import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TIPI_ORA } from "@/lib/tipiOra";

export function ModificaOra({ riga, dipendenti, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (riga)
      setForm({
        dipendente_id: riga.dipendente_id,
        data: (riga.data || "").slice(0, 10),
        ore: String(riga.ore),
        tipo: riga.tipo || "ordinario",
        luogo: riga.luogo || "",
        note: riga.note || "",
      });
  }, [riga]);

  const mut = useMutation({
    mutationFn: async (p) => (await api.put(`/ore/${riga.id}`, p)).data,
    onSuccess: () => { toast.success("Riga ore aggiornata"); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const salva = () => {
    const ore = Number(form.ore);
    if (!form.dipendente_id || !form.data || !(ore > 0)) return toast.error("Compila dipendente, data e ore");
    if (!form.luogo) return toast.error("Seleziona il luogo di lavoro");
    if (form.tipo === "ordinario" && ore > 8) return toast.error("Le ore ordinarie non possono superare 8 al giorno");
    mut.mutate({ ...form, ore });
  };

  return (
    <Dialog open={!!riga} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="modifica-ora-dialog">
        <DialogHeader><DialogTitle className="font-display">Modifica riga ore</DialogTitle></DialogHeader>
        {form && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Dipendente</Label>
              <Select value={form.dipendente_id} onValueChange={set("dipendente_id")}>
                <SelectTrigger data-testid="modifica-ora-dipendente"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={form.data} onChange={(e) => set("data")(e.target.value)} data-testid="modifica-ora-data" />
            </div>
            <div className="space-y-1.5">
              <Label>Ore</Label>
              <Input type="number" step="0.5" min="0" value={form.ore} onChange={(e) => set("ore")(e.target.value)} data-testid="modifica-ora-ore" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={set("tipo")}>
                <SelectTrigger data-testid="modifica-ora-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPI_ORA.map((t) => <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Luogo</Label>
              <Select value={form.luogo} onValueChange={set("luogo")}>
                <SelectTrigger data-testid="modifica-ora-luogo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="officina">Officina</SelectItem>
                  <SelectItem value="cantiere">Cantiere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Note</Label>
              <Input value={form.note} onChange={(e) => set("note")(e.target.value)} data-testid="modifica-ora-note" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="modifica-ora-annulla">Annulla</Button>
          <Button onClick={salva} disabled={mut.isPending} data-testid="modifica-ora-salva">{mut.isPending ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
